import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import {
  fetchCategories,
  toMarketGroup,
  keys,
  FEED_MARKETS_PREVIEW_LIMIT,
  type MarketGroup,
} from "@/lib/queries";
import { useEventsFeed } from "@/lib/useEventsFeed";
import { useChannelTickers } from "@/hooks/useChannelTickers";
import { useUrlState } from "@/lib/useUrlState";
import { useInfiniteScroll } from "@/lib/useInfiniteScroll";
import { Chip, Button, EmptyState } from "@/components/ui";
import { ContentTopBar } from "@/components/layout/ContentTopBar";
import { MarketCard } from "@/components/markets/MarketCard";
import { MarketCardSkeleton } from "@/components/markets/MarketCardSkeleton";
import { groupResolution } from "@/lib/outcomes";
import { Inbox, TriangleAlert } from "@/icons";
import { cn } from "@/lib/cn";
import { buildHead } from "@/lib/seo";

// Home feed tabs. "For you" (a newest-sort placeholder until personalisation
// exists) is hidden for now per SM-159 — only "Trending" shows. Re-add a
// { value: "foryou", labelKey: "home.tabs.forYou", sort: "new" } entry (plus
// the discover-namespace key) to bring it back. Labels are i18n keys resolved
// at render time so the tab bar follows the active language.
const FEED_TABS = [
  { value: "trending", labelKey: "home.tabs.trending", sort: "" },
] as const;

const PAGE = 12;

export const Route = createFileRoute("/_app/")({
  // Home feed — the site's front door, so it carries the bare tagline
  // rather than a "<page> — <brand>" title. SM-381.
  head: () => buildHead({ path: "/" }),
  validateSearch: (s: Record<string, unknown>): { q?: string; category_id?: string; status?: string; sort?: string } => ({
    q: typeof s.q === "string" ? s.q : undefined,
    category_id: typeof s.category_id === "string" ? s.category_id : undefined,
    status: typeof s.status === "string" ? s.status : undefined,
    sort: typeof s.sort === "string" ? s.sort : undefined,
  }),
  component: MarketsPage,
});

// sort key → server sort_by / sort_dir
function sortParams(sort: string): { by: string; dir: "asc" | "desc" | "" } {
  if (sort === "new") return { by: "created_at", dir: "desc" };
  if (sort === "closing") return { by: "trading_ends_at", dir: "asc" };
  if (sort === "name") return { by: "name", dir: "asc" };
  return { by: "", dir: "" };
}

function MarketsPage() {
  const { t } = useTranslation("discover");
  const { q } = Route.useSearch();
  const [categoryId, setCategoryId] = useUrlState("category_id", "");
  const [status] = useUrlState("status", "");
  const [sort, setSort] = useUrlState("sort", "");
  const sp = sortParams(sort);

  const categoriesQ = useQuery({
    queryKey: keys.categories(),
    queryFn: () => fetchCategories({ limit: 50, offset: 0 }),
  });
  const categories = categoriesQ.data?.items ?? [];
  const catLookup = useMemo(() => new Map(categories.map((c) => [c.category_id, c])), [categories]);

  // Event feed, paged by event count. useEventsFeed pages by offset (one
  // PAGE per scroll) rather than re-fetching an ever-growing window from
  // offset 0, so a long catalog is never shipped in a single fetch.
  // Fetch the full outcome set per event (marketsLimit) so each card
  // ranks by live Yes ask and previews the true top 2, not the first 2
  // by display_order.
  const feed = useEventsFeed(
    {
      q,
      category_id: categoryId || undefined,
      status: status || undefined,
      sortBy: sp.by || undefined,
      sortDir: sp.dir || undefined,
      marketsLimit: FEED_MARKETS_PREVIEW_LIMIT,
    },
    PAGE,
  );

  const groups: MarketGroup[] = useMemo(
    () =>
      feed.events
        .map((e) => {
          const cat = catLookup.get(e.category_id);
          return toMarketGroup(e, cat?.name, cat?.icon_url);
        })
        .filter((g) => g.markets.length > 0)
        // Fully-resolved events sink to the bottom; unresolved surface first.
        // (Array.prototype.sort is stable, so server order holds within a bucket.)
        .sort((a, b) => Number(groupResolution(a.markets).allResolved) - Number(groupResolution(b.markets).allResolved)),
    [feed.events, catLookup],
  );

  // Live tickers for every visible market, read from the ACTIVE MODE's
  // books (SM-483). useChannelTickers resolves the page's logical ids to
  // channel ids in one batched request, fetches and subscribes in channel
  // space, and hands back a lookup in logical space. CASH resolves to an
  // identity map with zero requests, so this feed makes exactly the calls
  // it always made.
  //
  // The SM-473 caveat banner that used to sit here is GONE with this
  // change: it said these were CASH prices under a GAME label, which was
  // true and is no longer. Leaving it up once the prices are right would
  // be a new lie in the other direction.
  const ids = useMemo(() => groups.flatMap((g) => g.markets.map((m) => m.market_id)), [groups]);
  const { tickerFor } = useChannelTickers(ids, { refetchInterval: 30_000, keepPrevious: true });

  const hasMore = feed.hasNextPage;
  const showSkeletons = feed.isLoading;
  // Infinite scroll: fetch the next page when the sentinel nears the viewport.
  const loadMoreRef = useInfiniteScroll(feed.loadMore, hasMore && !feed.isFetchingNextPage);

  return (
    <div className="pb-2">
      {/* sticky filter header — stays pinned while the feed scrolls under it.
          The wrapper keeps the full-bleed background + divider; the inner
          ContentTopBar sits at main's px-4 / lg:px-6 so the tabs and the
          bell/balance line up with the full-width card grid's left+right edges
          (the grid no longer adds extra inset — it spans the content column
          like Explore).
          -mt-4 pulls the bar flush to the scroll-pane top (cancels main's pt-4;
          the wrapper also drops its top padding) so it's already pinned at
          scrollTop 0. Without it the ~24px above the bar lives in the scroll
          area and slides away as you near the top, so the bar visibly hops
          between two positions (the "wiggle"). Desktop already did this via
          lg:-mt-4; we now apply it on mobile too.
          Opaque bg (was bg-bg/95 + backdrop-blur): the blur was barely visible
          at 95% and a blurred sticky bar adds its own faint scroll shimmer. */}
      <div data-topbar className="sticky top-0 z-20 -mx-4 -mt-4 mb-4 border-b border-divider bg-bg px-4 pb-3 pt-3 lg:-mx-6 lg:px-6 lg:pb-0 lg:pt-0">
        <ContentTopBar className="mb-0">
          {q ? (
            <p className="text-sm text-fg-muted">
              <Trans
                t={t}
                i18nKey="home.resultsFor"
                values={{ query: q }}
                components={{ mark: <span className="font-medium text-fg" /> }}
              />
            </p>
          ) : (
            // Tabs span the bar's full height on desktop so the active
            // indicator sits ON the bottom hairline (no dead band between the
            // underline and the divider); labels stay vertically centered.
            <div className="flex gap-5 lg:h-full">
              {FEED_TABS.map((tab) => {
                const active = (sort === "new" ? "foryou" : "trending") === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setSort(tab.sort)}
                    className={cn(
                      // lg:pt-3 nudges the label down toward its underline (sits
                      // a touch below the bar's dead-center, the Figma tab feel).
                      "relative flex items-center pb-2 font-display text-base font-semibold transition-colors lg:pb-0 lg:pt-3",
                      active ? "text-primary" : "text-fg-muted hover:text-fg",
                    )}
                  >
                    {t(tab.labelKey)}
                    {active && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary" />}
                  </button>
                );
              })}
            </div>
          )}
        </ContentTopBar>
        {categoryId && (
          <div className="mt-3 lg:pb-3">
            <Chip selected onClick={() => setCategoryId("")}>
              {catLookup.get(categoryId)?.name ?? t("category")} ✕
            </Chip>
          </div>
        )}
      </div>

      {feed.isError ? (
        <EmptyState
          icon={<TriangleAlert />}
          title={t("home.couldntLoadMarkets")}
          description={t("catalogUnreachable")}
          action={<Button onClick={feed.refetch}>{t("retry")}</Button>}
        />
      ) : showSkeletons ? (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-3 xl:gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <MarketCardSkeleton key={i} imageLed />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          icon={<Inbox />}
          title={t("home.noMarketsFound")}
          description={q || categoryId ? t("tryDifferentSearch") : t("checkBackSoon")}
          action={q || categoryId ? <Button variant="outline" onClick={() => setCategoryId("")}>{t("clearFilters")}</Button> : undefined}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-3 xl:gap-4">
            {groups.map((g) => (
              <MarketCard key={g.id} group={g} tickerFor={tickerFor} imageLed />
            ))}
          </div>
          {hasMore && (
            <div ref={loadMoreRef} className="mt-3 lg:mt-4">
              {feed.isFetchingNextPage && (
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-3 xl:gap-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <MarketCardSkeleton key={i} imageLed />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
